/**
 * Billing page state machine — derives one explicit state from subscription payload + profile.
 */

export const BILLING_STATES = {
  PAST_DUE: 'past_due',
  CANCELED_IN_PERIOD: 'canceled_in_period',
  ACTIVE: 'active',
  CANCELED_ENDED: 'canceled_ended',
  ASSIGNED_NO_SUB: 'assigned_no_sub',
  NEVER_SUBSCRIBED: 'never_subscribed',
};

function parseDate(value) {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value.seconds != null || value._seconds != null) {
    const sec = value.seconds ?? value._seconds;
    const d = new Date(sec * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isPeriodActive(currentPeriodEnd) {
  const end = parseDate(currentPeriodEnd);
  if (!end) return false;
  return end.getTime() > Date.now();
}

function isFreeTier(tier) {
  return !tier || tier === 'free';
}

function isAssignedTier(tier) {
  return tier && tier !== 'free';
}

/**
 * @param {object} input
 * @param {string|null} input.rawSubscriptionStatus - Firestore subscriptionStatus (null if unset)
 * @param {string|null} input.subscriptionId
 * @param {boolean} input.cancelAtPeriodEnd
 * @param {string|number|object|null} input.currentPeriodEnd
 * @param {string|null} input.effectiveTier
 * @param {string|number|object|null} [input.canceledAt]
 * @param {string} [input.resolvedSubscriptionStatus] - getSubscriptionStatus payload default ('canceled')
 */
export function deriveBillingState(input) {
  const {
    rawSubscriptionStatus = null,
    subscriptionId = null,
    cancelAtPeriodEnd = false,
    currentPeriodEnd = null,
    effectiveTier = 'free',
    canceledAt = null,
    resolvedSubscriptionStatus = null,
  } = input;

  const inCanceledPeriod = cancelAtPeriodEnd === true && isPeriodActive(currentPeriodEnd);

  if (resolvedSubscriptionStatus === 'past_due' || rawSubscriptionStatus === 'past_due') {
    return BILLING_STATES.PAST_DUE;
  }

  if (inCanceledPeriod) {
    return BILLING_STATES.CANCELED_IN_PERIOD;
  }

  const hasActiveSub =
    subscriptionId &&
    (rawSubscriptionStatus === 'active' ||
      rawSubscriptionStatus === 'trialing' ||
      resolvedSubscriptionStatus === 'active' ||
      resolvedSubscriptionStatus === 'trialing');

  if (hasActiveSub && !inCanceledPeriod) {
    return BILLING_STATES.ACTIVE;
  }

  const wasCanceled =
    rawSubscriptionStatus === 'canceled' || canceledAt != null;

  if (wasCanceled && !inCanceledPeriod) {
    return BILLING_STATES.CANCELED_ENDED;
  }

  if (
    !subscriptionId &&
    isAssignedTier(effectiveTier) &&
    rawSubscriptionStatus !== 'canceled'
  ) {
    return BILLING_STATES.ASSIGNED_NO_SUB;
  }

  return BILLING_STATES.NEVER_SUBSCRIBED;
}

/**
 * @param {string} state
 * @param {string} targetPlan
 * @param {string|null} effectivePlan
 * @param {boolean} creatingCheckout
 * @param {string|null} [periodEndFormatted]
 */
export function planButtonLabel(
  state,
  targetPlan,
  effectivePlan,
  creatingCheckout,
  periodEndFormatted = null
) {
  const label = targetPlan.charAt(0).toUpperCase() + targetPlan.slice(1);

  if (creatingCheckout) return 'Processing...';

  if (effectivePlan === targetPlan) {
    if (state === BILLING_STATES.CANCELED_IN_PERIOD && periodEndFormatted) {
      return `Current Plan (cancels ${periodEndFormatted})`;
    }
    if (
      state === BILLING_STATES.ACTIVE ||
      state === BILLING_STATES.CANCELED_IN_PERIOD
    ) {
      return 'Current Plan';
    }
  }

  if (
    state === BILLING_STATES.NEVER_SUBSCRIBED ||
    state === BILLING_STATES.CANCELED_ENDED ||
    state === BILLING_STATES.ASSIGNED_NO_SUB
  ) {
    return `Upgrade to ${label}`;
  }

  return `Switch to ${label}`;
}

/**
 * Whether plan button for target plan should be disabled (current plan locked).
 */
export function isPlanButtonDisabled(state, targetPlan, effectivePlan, creatingCheckout) {
  if (creatingCheckout) return true;
  if (effectivePlan !== targetPlan) return false;
  return (
    state === BILLING_STATES.ACTIVE ||
    state === BILLING_STATES.CANCELED_IN_PERIOD
  );
}

/**
 * @returns {{ showTrialEnded: boolean, showCanceledEnded: boolean }}
 */
export function statusBannerFlags(state, trialLapsed) {
  return {
    showTrialEnded:
      state === BILLING_STATES.NEVER_SUBSCRIBED && trialLapsed === true,
    showCanceledEnded: state === BILLING_STATES.CANCELED_ENDED,
  };
}

/**
 * @param {string} state
 * @param {{ canceledAtFormatted?: string|null, periodEndFormatted?: string|null }} dates
 */
export function cancelControlProps(state, dates = {}) {
  const { canceledAtFormatted = null, periodEndFormatted = null } = dates;

  switch (state) {
    case BILLING_STATES.ACTIVE:
      return {
        enabled: true,
        buttonLabel: 'Cancel Subscription',
        helperText: 'Service continues until the end of your billing period.',
      };
    case BILLING_STATES.CANCELED_IN_PERIOD:
      return {
        enabled: false,
        buttonLabel: canceledAtFormatted
          ? `Canceled ${canceledAtFormatted} · service until ${periodEndFormatted || 'period end'}.`
          : `Canceled · service until ${periodEndFormatted || 'period end'}.`,
        helperText: null,
      };
    case BILLING_STATES.CANCELED_ENDED:
      return {
        enabled: false,
        buttonLabel: canceledAtFormatted ? `Canceled ${canceledAtFormatted}.` : 'Canceled.',
        helperText: null,
      };
    case BILLING_STATES.ASSIGNED_NO_SUB:
      return {
        enabled: false,
        buttonLabel: 'No payment subscription on file.',
        helperText: null,
      };
    case BILLING_STATES.NEVER_SUBSCRIBED:
    default:
      return {
        enabled: false,
        buttonLabel: 'No active subscription.',
        helperText: null,
      };
  }
}

export function canceledEndedBannerText(canceledAtFormatted) {
  if (canceledAtFormatted) {
    return `Subscription canceled on ${canceledAtFormatted}. Choose a plan to resubscribe.`;
  }
  return 'Subscription canceled. Choose a plan to resubscribe.';
}

export function usesUpgradeLabels(state) {
  return (
    state === BILLING_STATES.NEVER_SUBSCRIBED ||
    state === BILLING_STATES.CANCELED_ENDED ||
    state === BILLING_STATES.ASSIGNED_NO_SUB
  );
}

export { isFreeTier, isAssignedTier, parseDate as parseBillingDate };
